import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Checkbox,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  LocalShipping as ShippingIcon,
} from '@mui/icons-material';
import {
  formatDate,
  formatPrice,
  getStatusColor,
  getStatusIcon,
  getStatusLabel
} from '../../utils/orderUtils';

const paymentMethodLabels = {
  card: 'Carte bancaire',
  cash: 'Espèces',
  check: 'Chèque',
  paypal: 'PayPal',
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
  bank_transfer: 'Virement bancaire',
};

const formatPaymentMethod = (order) => {
  if (!order) return '—';
  const method = order.paymentMethod || order.payment_method;
  if (method) {
    return paymentMethodLabels[method] || method;
  }
  const payment = order.payments?.[0];
  if (payment?.paymentMethod) {
    return paymentMethodLabels[payment.paymentMethod] || payment.paymentMethod;
  }
  if (payment?.payment_method) {
    return paymentMethodLabels[payment.payment_method] || payment.payment_method;
  }
  return '—';
};

/**
 * Composant pour le tableau des commandes
 */
const OrdersTable = ({
  orders,
  selectedOrders,
  onOrderSelect,
  onSelectAll,
  onViewDetails,
  onPrint,
  onDownloadInvoice,
  onToggleOrderSelection,
  onGenerateLabel,
  onStatusClick
}) => {
  const getCustomerName = (order) => {
    const userFirst = order.user?.first_name ?? order.user?.firstName ?? order.billingFirstName ?? order.deliveryFirstName ?? '';
    const userLast = order.user?.last_name ?? order.user?.lastName ?? order.billingLastName ?? order.deliveryLastName ?? '';
    const full = `${userFirst} ${userLast}`.trim();
    return full || 'Client';
  };

  return (
    <TableContainer component={Paper} sx={{ mb: 3 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={selectedOrders.size > 0 && selectedOrders.size < orders.length}
                checked={selectedOrders.size === orders.length && orders.length > 0}
                onChange={onSelectAll}
                sx={{
                  color: 'text.secondary',
                  '&.Mui-checked': {
                    color: 'primary.main',
                  },
                  '&.MuiCheckbox-indeterminate': {
                    color: 'primary.main',
                  },
                }}
              />
            </TableCell>
            <TableCell>Numéro de commande</TableCell>
            <TableCell>Client</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Statut</TableCell>
            <TableCell>Moyen de paiement</TableCell>
            <TableCell>Total</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map((order) => (
            <TableRow
              key={order.id}
              hover
              selected={selectedOrders.has(order.id)}
              onClick={(event) => onOrderSelect(order.id, event)}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                '&.Mui-selected': {
                  backgroundColor: '#4f4f4f',
                  '&:hover': {
                    backgroundColor: '#4f4f4f',
                  },
                },
              }}
            >
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedOrders.has(order.id)}
                  onChange={() => { }} // Géré par onClick
                  onClick={(event) => {
                    console.log('Checkbox onClick for order:', order.id);
                    event.stopPropagation();
                    onToggleOrderSelection(order.id);
                  }}
                  sx={{
                    color: 'text.secondary',
                    '&.Mui-checked': {
                      color: 'primary.main',
                    },
                  }}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight="medium">
                  {order.orderNumber}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {getCustomerName(order)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {formatDate(order.createdAt)}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  icon={getStatusIcon(order.status)}
                  label={getStatusLabel(order.status)}
                  color={getStatusColor(order.status)}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onStatusClick) onStatusClick(order.id);
                  }}
                  sx={{ cursor: 'pointer' }}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {formatPaymentMethod(order)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight="medium">
                  {formatPrice(order.totalAmount)}
                </Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Voir les détails">
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        onViewDetails(order.id);
                      }}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Imprimer la commande">
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        onPrint(order.id);
                      }}
                    >
                      <PrintIcon />
                    </IconButton>
                  </Tooltip>
                  {order.status === 'confirmed' && (
                    <Tooltip title="Télécharger la facture">
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          onDownloadInvoice(order.id);
                        }}
                        sx={{ color: 'success.main' }}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {order.status === 'confirmed' && order.shippingMethodCode && (
                    <Tooltip title={order.trackingNumber ? "Télécharger l'étiquette" : "Générer l'étiquette"}>
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          onGenerateLabel(order.id);
                        }}
                        sx={{ color: order.trackingNumber ? 'info.main' : 'warning.main' }}
                      >
                        <ShippingIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default OrdersTable;
